import {
  FocusEvent,
  FormEventHandler,
  Fragment,
  useRef,
  useState,
} from 'react';
import intl from 'react-intl-universal';
import { useDispatch } from 'react-redux';
import {
  Button,
  Icon,
  TextField,
  Typography,
  useLink,
} from '@getsynapse/design-system';
import Auth from '@aws-amplify/auth';
import { Link } from 'react-router-dom';
import { showNotificationBanner } from 'state/InlineNotification/inlineNotificationSlice';
import { EMAIL_REGEX, PATHS } from 'utils/constants';

const SendEmailForm = ({ onSubmit }: { onSubmit: (sent: boolean) => void }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [inputState, setInputState] = useState<'default' | 'error'>('default');
  const [errorMessage, setErrorMessage] = useState<string>();
  const [disabled, setDisabled] = useState<boolean>(true);
  const ref = useRef<HTMLInputElement>(null);

  const validateEmail = (email: string) => {
    const isEmpty = !Boolean(email);
    const isEmail = EMAIL_REGEX.test(email);
    setDisabled(isEmpty || !isEmail);
    if (!isEmail) {
      setErrorMessage(intl.get('ERROR_MESSAGE.INVALID_FORMAT'));
      setInputState('error');
    } else {
      setErrorMessage(undefined);
      setInputState('default');
    }
  };

  const handleSubmit: FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();
    setLoading(true);
    const email: string = ref.current?.value || '';
    try {
      await Auth.forgotPassword(email);
      onSubmit(true);
    } catch (error) {
      const message = intl.get('ERROR_MESSAGE.USER_NOT_REGISTERED');
      dispatch(
        showNotificationBanner({
          notificationVariant: 'error',
          notificationText: message,
          timeout: 7000,
        })
      );
      setErrorMessage(message);
      setInputState('error');
      setLoading(false);
    }
  };

  const checkEmail = () => {
    validateEmail(ref.current?.value!);
    setInputState('default');
    setErrorMessage(undefined);
  };

  return (
    <Fragment>
      <Typography variant='body' className='mb-6 text-neutral-black'>
        {intl.get('FORGOT_PAGE.PROMPT')}
      </Typography>

      <form onSubmit={handleSubmit}>
        <TextField
          ref={ref}
          label={intl.get('FORGOT_PAGE.INPUT.LABEL')}
          placeholder={intl.get('FORGOT_PAGE.INPUT.PLACEHOLDER')}
          className='mb-6'
          onBlur={(event: FocusEvent<HTMLInputElement>) =>
            validateEmail(event.target.value)
          }
          onChange={checkEmail}
          state={inputState}
          helpText={errorMessage}
          data-cy='forgot-password-input__email'
        />

        <Button
          type='submit'
          className='w-full justify-center'
          disabled={disabled}
          loading={loading}
          data-cy='forgot-password-submit__button'
        >
          {intl.get('FORGOT_PAGE.BUTTON')}
        </Button>
      </form>
    </Fragment>
  );
};

const SentEmailMessage = ({
  resetPage,
}: {
  resetPage: (sent: boolean) => void;
}) => {
  const internalLink = useLink();

  return (
    <Fragment>
      <div className='flex mb-4'>
        <div className='w-6'>
          <Icon
            name='checkmark-circle'
            className='text-success-dark text-2xl'
          />
        </div>

        <Typography className='ml-2 text-neutral-black'>
          {intl.get('FORGOT_PAGE.SUCCESS_MESSAGE')}
        </Typography>
      </div>
      <div className='flex'>
        <Link
          to={PATHS.FORGOT_PASSWORD}
          onClick={() => resetPage(false)}
          className={internalLink}
        >
          {intl.get('FORGOT_PAGE.FAIL_PROMPT')}
        </Link>
        <p className='ml-2'>{intl.get('FORGOT_PAGE.FAIL_PROMPT_TEXT')}</p>
      </div>
    </Fragment>
  );
};

const ForgotPassword = () => {
  const [sent, setSent] = useState<boolean>(false);

  return (
    <div>
      <Typography variant='h2' className='mb-4 text-neutral-black'>
        {intl.get('FORGOT_PAGE.TITLE')}
      </Typography>

      {!sent && <SendEmailForm onSubmit={setSent} />}

      {sent && <SentEmailMessage resetPage={setSent} />}
    </div>
  );
};

export default ForgotPassword;
